import { socialImageMeta } from "@/lib/social-sharing";
import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Search } from "lucide-react";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { hreflangLinks } from "@/lib/seo";
import { pageTitle } from "@/lib/brand";
import { isPilot, pilotSlug } from "@/lib/lexicon-pilot";

export type Lang = "en" | "tr";

export type Category = "yz" | "surd" | "insan" | "ilet" | "iklim";

type Entry = {
  id: string;
  term: { tr: string; en: string };
  short: { tr: string; en: string };
  body: { tr: string; en: string };
  cats: Category[];
  source?: { tr: string; en: string };
  related?: { slug: string; label: { tr: string; en: string }; to: "yazilar" | "dergi" }[];
};

const CATEGORY_LABEL: Record<Category, { tr: string; en: string }> = {
  yz: { tr: "Yapay Zeka", en: "AI" },
  surd: { tr: "Sürdürülebilirlik", en: "Sustainability" },
  insan: { tr: "İnsan & Gelecek", en: "Human & Future" },
  ilet: { tr: "İletişim", en: "Communication" },
  iklim: { tr: "İklim", en: "Climate" },
};


export const ENTRIES: Entry[] = [
  {
    id: "algoritmik-gorunurluk-ekonomisi",
    term: { tr: "Algoritmik Görünürlük Ekonomisi", en: "Algorithmic Visibility Economy" },
    cats: ["ilet", "yz"],
    short: {
      tr: "Kamusal tartışmanın ritmini akıl yürütme değil platformların katılım optimizasyonu belirler.",
      en: "Public debate is paced not by careful reasoning but by platforms' engagement-optimisation logic.",
    },
    body: {
      tr: "Kamusal tartışmanın ritmi ve içeriği, dikkatli akıl yürütme yerine platformların katılım optimizasyon mantığı tarafından şekillenir. Doğruluk değil görünürlük, derinlik değil yayılma hızı belirleyici kriter haline gelir. Duygusal ve çatışmaya dayalı içerik algoritmik olarak öne çıktığında, savaş muhabirliğinden iklim iletişimine kadar her alan bu ekonominin siyasi uzamına dönüşür.",
      en: "Pace and content of public debate are shaped by engagement optimisation rather than reasoning. Not accuracy but visibility, not depth but spreading speed becomes the decisive metric. When emotionally charged and conflict-driven content is algorithmically promoted, every field — from war reporting to climate communication — turns into a political extension of this economy.",
    },
  },
  {
    id: "algoritmik-teslimiyet",
    term: { tr: "Algoritmik Teslimiyet", en: "Algorithmic Surrender" },
    cats: ["yz"],
    short: {
      tr: "Kararı kolaylık adına makineye devretmenin sessiz, gönüllü hali.",
      en: "The quiet, voluntary handover of decision to the machine in the name of convenience.",
    },
    body: {
      tr: "Hangi şarkıyı, hangi haberi, hangi rotayı, hangi adayı seçeceğimize artık biz değil model karar verir; ve bu çoğunlukla rahatsız etmez. Algoritmik teslimiyet, baskıyla değil konforla işleyen bir özgürlük kaybıdır: tercih sandığımız şey, kişiselleştirilmiş bir öneridir.",
      en: "Which song, which headline, which route, which candidate — the model, not us, decides; and it rarely feels uncomfortable. Algorithmic surrender is a loss of freedom that runs not on coercion but on convenience: what we take to be a choice is a personalised recommendation.",
    },
    related: [
      { slug: "algoritmik-doga", label: { tr: "Algoritmik Doğa", en: "Algorithmic Nature" }, to: "dergi" },
      { slug: "dijital-leviathan", label: { tr: "Dijital Leviathan", en: "Digital Leviathan" }, to: "dergi" },
    ],
  },
  {
    id: "askida-olan-ozne",
    term: { tr: "Askıda Olan Özne", en: "Suspended Subject" },
    cats: ["insan", "ilet"],
    short: {
      tr: "Karar verme, sorumluluk alma ve eyleme geçme kapasitesinin süresiz olarak ertelendiği yeni bir özne kipi.",
      en: "A mode of subjectivity in which the capacity to decide, take responsibility, and act is indefinitely postponed.",
    },
    body: {
      tr: "Karar verecek ama önce daha çok veri gerekiyor; sorumluluk alacak ama önce başkalarını beklemek lazım; eyleme geçecek ama önce \"doğru an\"ı kollamak gerek. Askıda olan özne iradesizlik değildir; süresiz teenninin normalleştiği bir sujektivite kipidir. Platformlar, kurumlar ve iklim söylemi bu askıyı üretir: her karar başka bir uzmanın, başka bir raporun, başka bir mutabakatın gelmesine bağlanır. Sonuç, sürekli hazır ama hiç harekete geçmeyen bir öznedir — failliğini kaybetmez, yalnızca erteler.",
      en: "About to decide, but more data is needed first; about to take responsibility, but others must be waited on; about to act, but the \"right moment\" must be timed. The suspended subject is not indecisive; it is a subjectivity in which indefinite deferral has become the norm. Platforms, institutions, and climate discourse produce this suspension: every decision is tied to another expert, another report, another consensus. What remains is a subject perpetually ready but never in motion — agency intact, only postponed.",
    },
  },
  {
    id: "ayrisma-tezi",
    term: { tr: "Ayrışma Tezi (Decoupling)", en: "Decoupling Thesis" },
    cats: ["surd", "iklim"],
    short: {
      tr: "Ekonomik büyüme ile kaynak tüketimi arasındaki bağın koparılabileceği iddiası.",
      en: "The claim that economic growth can be decoupled from resource consumption.",
    },
    body: {
      tr: "Teknoloji optimistlerinin ve ana akım çevre iktisadının temel argümanı: verimlilik yeterince ilerlerse, şebekeler karbonsuzlaşırsa ve yapay zekâ optimizasyona koşulursa, büyüme kaynak tüketiminden ayrışabilir. Tez teorik olarak tutarlıdır; ancak yalnızca sistemin toplam ölçeği görece sabit ya da doğrusal büyürken işler. Üstel ölçek karşısında ayrışma vaadi, doygunluk noktasına ulaşılmadan yeni talep dalgaları tarafından sürekli ertelenir.",
      en: "The core argument of technology optimists and mainstream environmental economics: with enough efficiency, grid decarbonisation, and AI-driven optimisation, growth can be decoupled from resource use. The thesis is internally consistent — but holds only while the system's total scale stays roughly stable or grows linearly. Against exponential scale, the promise of decoupling is perpetually postponed by fresh waves of demand before any saturation point is reached.",
    },
    related: [
      { slug: "verimlilik-maskeli-obur", label: { tr: "Verimlilik maskeli obur!", en: "The efficiency-masked glutton" }, to: "yazilar" },
    ],
  },
  {
    id: "bagimlilik-insasi",
    term: { tr: "Bağımlılık İnşası", en: "Dependency Construction" },
    cats: ["surd", "iklim"],
    short: {
      tr: "ESG danışmanlığının kurumsal bilgiyi içselleştirmek yerine dışarıdan kiralanan bir yapıya dönüştürmesi.",
      en: "ESG consultancy turning corporate knowledge into a rented external structure rather than internalising it.",
    },
    body: {
      tr: "Sürdürülebilirlik danışmanlığı ve ESG uyum hizmetleri sektörü, kurumsal bilgiyi içselleştirmek yerine dışarıdan kiralanan bir yapıya dönüştürür. Hizmet sağlayıcı ayrıldığında kurumsal hafıza da yok olur. Sektörün yapısal teşviki: sorun çözüldüğünde değil, sorun sürdüğünde büyüyen model. Bağımlılık yan ürün değil, tasarım özelliğidir.",
      en: "The sustainability consultancy and ESG compliance industry transforms corporate knowledge into a rented external structure rather than something internalised. When the provider leaves, institutional memory disappears with them. The sector's structural incentive: a model that grows not when the problem is solved but when it persists. Dependency is not a by-product but a design feature.",
    },
  },
  {
    id: "baglam-kaybi",
    term: { tr: "Bağlam Kaybı", en: "Context Loss" },
    cats: ["ilet"],
    short: {
      tr: "Aynı görüntünün taşındığı platforma göre kökten farklı anlamlar üretmesi.",
      en: "The same image producing radically different meanings depending on the platform that carries it.",
    },
    body: {
      tr: "Bir bilgi biriminin farklı ağlarda dolaşırken orijinal bağlamından kopması; aynı görüntünün ya da verinin taşındığı platforma göre kökten farklı anlamlar üretmesi. Dezenformasyonun üretilmediği, yalnızca bağlamın değiştirildiği durum. Deepfake teknolojisi bağlam kaybını sanayi ölçeğine taşıdı: özgün materyal bile sahte olarak değerlendirilebilir hale geldi.",
      en: "A unit of information detaches from its original context as it travels across networks; the same image or datum produces radically different meanings depending on where it lands. No disinformation is manufactured — only the context is swapped. Deepfake technology has industrialised context loss: even authentic material can now be dismissed as fake.",
    },
  },
  {
    id: "baglanti-paradoksu",
    term: { tr: "Bağlantı Paradoksu", en: "Connection Paradox" },
    cats: ["insan"],
    short: {
      tr: "En yüksek bağlantı kapasitesinin yapısal yalnızlığın en yüksek düzeyiyle örtüşmesi.",
      en: "Peak technological connectivity coinciding with peak structural loneliness.",
    },
    body: {
      tr: "Tarihsel olarak en yüksek teknolojik bağlantı kapasitesine sahip olduğumuz çağda yapısal yalnızlık da en yüksek düzeye ulaşır. Ağ genişliği ile ilişki derinliği arasındaki makas açılır. Gen Z ve Millennials en dijital bağlantılı kuşaklar olmasına rağmen en yalnız olanlardır.",
      en: "In the most technologically connected age in history, structural loneliness reaches its highest point. The gap between network breadth and relational depth widens. Gen Z and Millennials are the most digitally connected generations — and also the loneliest.",
    },
  },
  {
    id: "bilgi-toksikolojisi",
    term: { tr: "Bilgi Toksikolojisi", en: "Information Toxicology" },
    cats: ["yz", "ilet"],
    short: {
      tr: "Aşırı bilgi yükünün, bilgi yokluğu kadar karar kalitesini bozması.",
      en: "Information overload damaging decision quality as much as the absence of information does.",
    },
    body: {
      tr: "Aşırı bilgi yükünün bilgi yokluğu kadar karar kalitesini bozduğu paradoksal durum. Doz ile zehir arasındaki sınırın bilgiye uygulanması: belirli bir eşiğin ötesinde bilgi, karar mekanizmasını felç eder. \"Henüz yeterli veriye sahip değiliz\" söylemi bu toksikolojinin en rafine tezahürüdür.",
      en: "The paradox in which information overload degrades decision quality as severely as information scarcity. The dose–poison threshold applied to knowledge: past a certain point, information paralyses the decision mechanism. \"We don't yet have enough data\" is the most refined expression of this toxicology.",
    },
  },
  {
    id: "dayaniklilik-fetisizmi",
    term: { tr: "Dayanıklılık Fetişizmi", en: "Resilience Fetishism" },
    cats: ["surd", "iklim", "insan"],
    short: {
      tr: "Sistemi sorgulamak yerine bireyi ve kurumları sistemin hasarına dayanmaya zorlayan söylem.",
      en: "A discourse that, instead of questioning the system, forces individuals and institutions to endure its damage.",
    },
    body: {
      tr: "\"Dayanıklı ol\" çağrısı iklim krizinden burnout kültürüne, gıda güvensizliğinden hibrit çalışmanın normalleştirilmesine kadar her yeri kaplar. Dayanıklılık fetişizmi zararı azaltmakla değil, zarara katlanma kapasitesini genişletmekle ilgilenir. \"Neden bu kadar kırılganız?\" sorusu yerine \"nasıl dayanırız?\" sorusu merkeze alınır. Bu, güç yapılarının değişmeden kalmasını sağlayan sessiz bir stratejidir: dayanıklılık ne kadar övülürse, dayanılan şey o kadar sorgulanmaz hale gelir. Öznenin adaptasyonu, sistemin dokunulmazlığına dönüşür.",
      en: "The call to \"be resilient\" spreads from the climate crisis to burnout culture, from food insecurity to the normalisation of hybrid work. Resilience fetishism is not about reducing harm but about expanding the capacity to bear it. \"Why are we this fragile?\" is displaced by \"how do we endure?\" It is a quiet strategy that keeps power structures intact: the more resilience is praised, the less what is endured gets examined. The subject's adaptation becomes the system's immunity.",
    },
  },
  {
    id: "dijital-leviathan",
    term: { tr: "Dijital Leviathan", en: "Digital Leviathan" },
    cats: ["yz", "ilet"],
    short: {
      tr: "Hobbes'un devletinin yerini alan, ondan daha az sorumlu yeni egemen.",
      en: "A new sovereign that replaces Hobbes's state — and answers to far less.",
    },
    body: {
      tr: "Birkaç platform, milyarlarca insan için kimlik, ödeme, iletişim, hatırlama ve görünürlük altyapısını işletir. Dijital Leviathan, yasayla değil API ile yönetir; vatandaşlık hakları yerine kullanım koşulları, mahkeme yerine moderasyon panelleri vardır.",
      en: "A handful of platforms run the infrastructure of identity, payment, communication, memory, and visibility for billions. The Digital Leviathan rules not through law but through API; instead of citizenship rights there are terms of service, and instead of courts, moderation panels.",
    },
    related: [
      { slug: "dijital-leviathan", label: { tr: "Dijital Leviathan", en: "Digital Leviathan" }, to: "dergi" },
    ],
  },
  {
    id: "dijital-yerinden-edilme",
    term: { tr: "Dijital Yerinden Edilme", en: "Digital Displacement" },
    cats: ["yz", "insan"],
    short: {
      tr: "YZ otomasyonunun beyaz yakalı bilişsel emeği de ikame etmesi.",
      en: "AI automation displacing white-collar cognitive labour as well.",
    },
    body: {
      tr: "YZ otomasyonunun yalnızca mavi yakalı değil beyaz yakalı bilişsel emeği de ikame etmesiyle gerçekleşen istihdam dönüşümü. Tehdit altındaki şey yalnızca iş değil; kimlik, anlam ve karar sahipliği. 18. yüzyılda makineler fiziksel emeğin değerini düşürdü; günümüzde YZ akıl yürütmeyi, tahmin etmeyi ve yaratmayı otomatikleştiriyor.",
      en: "An employment transformation in which AI automation substitutes for cognitive white-collar labour, not just blue-collar work. What is threatened is not only the job but identity, meaning and ownership of decisions. In the 18th century machines devalued physical labour; today AI automates reasoning, prediction and creation.",
    },
  },
  {
    id: "dikkat-madenciligi",
    term: { tr: "Dikkat Madenciliği", en: "Attention Mining" },
    cats: ["yz", "insan"],
    short: {
      tr: "İnsan dikkatinin bir doğal kaynak gibi çıkarıldığı ve tükendiği endüstri.",
      en: "An industry in which human attention is extracted and depleted like a natural resource.",
    },
    body: {
      tr: "Bildirim, otomatik oynatma, sonsuz akış ve mikro etkileşim — hepsi dikkati cevherden ayırma yöntemleridir. Dikkat madenciliği, fosil yakıt mantığını bilince taşır: kazanç bugün, çevresel hasar (uyku, odak, ruh sağlığı) zamana yayılır.",
      en: "Notifications, autoplay, infinite scroll, and micro-interactions — all are methods for separating attention from its ore. Attention mining carries fossil-fuel logic into consciousness: the gains accrue today, the environmental damage (sleep, focus, mental health) is amortised across time.",
    },
  },
  {
    id: "ekolojik-burjuvazi",
    term: { tr: "Ekolojik Burjuvazi", en: "Ecological Bourgeoisie" },
    cats: ["surd", "iklim"],
    short: {
      tr: "Çevresel kaygıyı kültürel sermaye ve sınıf ayrımı aracı olarak kullanan kesim.",
      en: "A class that turns environmental concern into cultural capital and class distinction.",
    },
    body: {
      tr: "Tesla kullanmak, organik alışveriş yapmak, sıfır atık yaşam tarzını benimsemek yeni bir yeşil elit habitusu oluşturuyor. Bourdieu'nun habitus kavramının ekolojik bağlama uygulanması. Bu habitus ekolojik krizi bireysel yaşam tarzı tercihlerine indirger; kolektif siyasi aciliyetini arındırır.",
      en: "Driving a Tesla, shopping organic, adopting a zero-waste lifestyle form a new green elite habitus. Bourdieu's concept applied to ecology. This habitus reduces the ecological crisis to individual lifestyle choices and strips away its collective political urgency.",
    },
  },
  {
    id: "ekolojik-simulasyon",
    term: { tr: "Ekolojik Simülasyon", en: "Ecological Simulation" },
    cats: ["surd", "iklim"],
    short: {
      tr: "Sürdürülebilirlik söyleminin gerçek etki yerine \"önemseme duygusu\" üretmesi.",
      en: "Sustainability discourse producing a feeling of care instead of real impact.",
    },
    body: {
      tr: "Sürdürülebilirlik söyleminin gerçek ekolojik etkiyi değiştirmeksizin önemseme duygusunu ürettiği hiper-gerçeklik düzeni. Baudrillard'ın simülasyon kavramının yeşil pazarlama estetiğine uygulanması. Eko-etiketli markalar, içi boş karbon nötr vaatler: gerçek ekolojik dengeyi değil, yalnızca görüntüsünü üretirler.",
      en: "A hyperreal order in which sustainability discourse produces the feeling of caring without changing actual ecological impact. Baudrillard's simulation applied to green marketing aesthetics. Eco-labelled brands and empty carbon-neutral pledges produce not ecological balance but its image — and substitute for the real change they invoke.",
    },
  },
  {
    id: "enerji-liderligi",
    term: { tr: "Enerji Liderliği", en: "Energy Leadership" },
    cats: ["iklim", "yz"],
    short: {
      tr: "Yapay zekâ çağında jeopolitik gücün en zeki modeli değil, kesintisiz elektriği üretebileni belirlemesi.",
      en: "In the AI era, geopolitical power is set not by who writes the smartest model but by who can supply uninterrupted electricity.",
    },
    body: {
      tr: "Küresel teknoloji yarışı, modellerin parametre sayısından çok onları besleyecek elektriğin üretimi, depolanması ve şebeke kapasitesi üzerinden veriliyor. ABD'nin kapatılan nükleer ve kömür santrallerini yeniden devreye alması, Çin'in bütünleşik siber-enerji ekosistemi, Körfez ülkelerinin güneş + sermaye paketleri bu yeni denklemin işaretleri. Enerjisini üretebilen güçler veri-enerji çağının ev sahibi, dışa bağımlı olanlar ise kiracı konumuna düşüyor.",
      en: "The global tech race is decided less by parameter counts than by who can produce, store, and grid-deliver the electricity to run the models. The United States bringing retired nuclear and coal plants back online, China's integrated cyber-energy ecosystem, and the Gulf states' bundling of solar plus capital are signals of this new equation. Powers that produce their own energy become hosts of the data-energy age; those dependent on imports are reduced to tenants.",
    },
    related: [
      { slug: "verimlilik-maskeli-obur", label: { tr: "Verimlilik maskeli obur!", en: "The efficiency-masked glutton" }, to: "yazilar" },
    ],
  },
  {
    id: "epistemik-bagimlilik",
    term: { tr: "Epistemik Bağımlılık", en: "Epistemic Dependency" },
    cats: ["yz"],
    short: {
      tr: "Karar verdiği bilginin kaynağına erişemeyen, doğrulayamayan ve itiraz edemeyen birey.",
      en: "An individual unable to access, verify or contest the source of the knowledge they decide on.",
    },
    body: {
      tr: "Bireyin karar üretirken dayandığı bilginin kaynağına erişemediği, doğrulayamadığı ve itiraz edemediği durum. Habermas'ın gerekçelendirilebilir akıl yürütme idealinin algoritmik çağdaki çöküşü. Kredi skorları, işe alım algoritmaları, öneri motorları: birey karar verir ama bilgi zeminine erişemez. Özerklik görünürde korunmuş, özde teslim edilmiştir.",
      en: "A condition in which the individual cannot access, verify or contest the source of the knowledge they decide on. The collapse, in the algorithmic age, of Habermas's ideal of justifiable reasoning. Credit scores, hiring algorithms, recommender systems: the individual decides, but the epistemic ground is closed. Autonomy is preserved in appearance, surrendered in substance.",
    },
  },
  {
    id: "epistemik-silahlanma",
    term: { tr: "Epistemik Silahlanma", en: "Epistemic Weaponisation" },
    cats: ["ilet", "yz"],
    short: {
      tr: "Gerçeğin içeriğini değil statüsünü hedef alan enformasyon stratejisi.",
      en: "An information strategy targeting the status of truth, not its content.",
    },
    body: {
      tr: "Hangi bilginin güvenilir, hangi kaynağın meşru sayılacağını çökertmeyi amaçlayan yapısal müdahale. Yalan yaymak değil, hakikate duyulan güveni yıkmak. Klasik propaganda belirli iddiaları çürütmeye çalışırdı; epistemik silahlanma doğrulama mekanizmasının kendisini hedef alır. Sonuç spesifik bir yanlış inanç değil, genel güvensizlik halidir.",
      en: "A structural intervention aimed at collapsing which knowledge counts as reliable and which source counts as legitimate. Not spreading lies but eroding trust in truth itself. Classical propaganda tried to refute specific claims; epistemic weaponisation targets the verification mechanism itself. The output is not a specific false belief but generalised distrust.",
    },
    related: [
      { slug: "hakikat-ve-guven-bilginin-savas-alani", label: { tr: "Hakikat ve Güven", en: "Truth and Trust" }, to: "yazilar" },
    ],
  },
  {
    id: "filtrelenen-kiyamet",
    term: { tr: "Filtrelenen Kıyamet", en: "Filtered Apocalypse" },
    cats: ["iklim", "ilet"],
    short: {
      tr: "Felaketin gerçek olmadığı için değil, estetize edildiği için inanılmaz hale gelmesi.",
      en: "Catastrophe becoming unbelievable not because it isn't real, but because it has been aestheticised.",
    },
    body: {
      tr: "Yangın, sel ve savaş görüntüleri akışın bir parçası olduğunda, izleyici dehşeti değil kompozisyonu hatırlar. Filtrelenen kıyamet, gerçeğin yokluğu değil; sürekli, güzel ve sayfa altında olmasıdır. Aciliyet, bir sonraki kaydırmaya kadar dayanır.",
      en: "When fire, flood, and war become part of the feed, the viewer remembers not the horror but the composition. The filtered apocalypse is not the absence of reality; it is reality kept continuous, beautiful, and below the fold. Urgency lasts until the next scroll.",
    },
    related: [
      { slug: "filtrelenen-kiyamet", label: { tr: "Filtrelenen Kıyamet", en: "Filtered Apocalypse" }, to: "dergi" },
    ],
  },
  {
    id: "guven-ekonomisi",
    term: { tr: "Güven Ekonomisi", en: "Trust Economy" },
    cats: ["ilet", "surd"],
    short: {
      tr: "Ekonomik değerin üretim kapasitesinden değil belirsizliği yönetme kapasitesinden türemesi.",
      en: "Economic value derived from the capacity to manage uncertainty, not from production capacity.",
    },
    body: {
      tr: "Ekonomik değerin üretim kapasitesinden değil belirsizliği yönetme kapasitesinden türediği piyasa düzeni. Ürünün yanı sıra güvenilirlik, tutarlılık ve öngörülebilirlik satın alınır. Luhmann'ın güven kavramının piyasaya uygulanması. Kalite artık rekabet avantajı değil geçilmesi gereken bir taban; farkı güvenilirlik belirler.",
      en: "A market order in which economic value derives from the capacity to manage uncertainty rather than from production capacity. What is bought is not only the product but reliability, consistency and predictability. Luhmann's notion of trust applied to markets. Quality is no longer a competitive edge but a baseline; what differentiates is trustworthiness.",
    },
  },
  {
    id: "guven-sozlesmesi",
    term: { tr: "Güven Sözleşmesi", en: "Trust Contract" },
    cats: ["iklim", "ilet"],
    short: {
      tr: "Her kolektif dönüşümün altındaki örtük \"verilen sözler tutulacak\" mutabakatı.",
      en: "The implicit \"promises will be kept\" pact underlying every collective transformation.",
    },
    body: {
      tr: "İklim eylemi dahil her kolektif dönüşümün altında yatan örtük toplumsal mutabakat: hükümetler, şirketler, bilim ve vatandaşlar arasında \"verilen sözler tutulacak\" beklentisi. Greenwashing bu sözleşmeyi bozan birincil aktördür. Sözleşme kırıldığında en gerçek eylemler bile şüpheyle karşılanır.",
      en: "The implicit social pact beneath every collective transformation, including climate action: the expectation, among governments, firms, science and citizens, that promises will be kept. Greenwashing is the primary breach of this contract. Once it is broken, even genuine actions are met with suspicion.",
    },
  },
  {
    id: "hakikat-enflasyonu",
    term: { tr: "Hakikat Enflasyonu", en: "Truth Inflation" },
    cats: ["ilet", "yz"],
    short: {
      tr: "Her şeyin doğru sayıldığı bir ortamda doğruluğun değer kaybetmesi.",
      en: "The devaluation of truth in an environment where everything counts as true.",
    },
    body: {
      tr: "Üretken modeller, içerik fabrikaları ve gerçeklik filtreleri arz tarafından sınırsız \"bilgi\" üretir. Hakikat enflasyonu, yalanın bolluğundan değil hakikatin sıradanlaşmasından kaynaklanır; gerçek, kanıtlanabilir olduğu için değil paylaşıldığı için kabul görür.",
      en: "Generative models, content farms and reality filters produce unlimited \"knowledge\" on the supply side. Truth inflation comes not from an abundance of lies but from the banalisation of truth; the real is accepted not because it can be proven but because it has been shared.",
    },
  },
  {
    id: "homo-technicus",
    term: { tr: "Homo Technicus", en: "Homo Technicus" },
    cats: ["yz", "insan"],
    short: {
      tr: "Aracı kullanmakla yetinmeyip aracın içinden düşünen yeni insan tipi.",
      en: "A new human type that no longer merely uses the tool but thinks from inside it.",
    },
    body: {
      tr: "Akıllı telefon, GPS, öneri motoru ve büyük dil modelleri günlük bilişin uzantısı haline geldiğinde, akıl bedene değil cihaza yaslanır. Homo technicus, biyolojik bir tür değil, bilişsel bir bağımlılık konumudur; bu konumun etiği henüz yazılmadı.",
      en: "Once smartphones, GPS, recommendation engines and large language models become extensions of daily cognition, reason leans on the device rather than the body. Homo technicus is not a biological species but a cognitive posture of dependency; the ethics of that posture have not yet been written.",
    },
    related: [
      { slug: "the-diplomats-last-message", label: { tr: "Diplomatın Son Mesajı", en: "The Diplomat's Last Message" }, to: "dergi" },
    ],
  },
  {
    id: "ikinci-el-anlati",
    term: { tr: "İkinci El Anlatı", en: "Second-Hand Story" },
    cats: ["ilet", "iklim"],
    short: {
      tr: "Muhatabını değil, muhatabının bir sonraki muhatabını ikna etmek üzere tasarlanmış iletişim.",
      en: "Communication designed to persuade not the person in front of you, but the next person they will talk to.",
    },
    body: {
      tr: "Karşınızdaki yatırımcıyı ikna ettiğinizde işiniz bitmez; o yatırımcı hikâyenizi bir yatırım komitesine, komite bir risk birimine, risk birimi bir yönetim kuruluna taşır — sizin hiç girmeyeceğiniz odalarda, sizden daha az ikna olmuş insanlar tarafından, sizin cümlelerinizden daha kötü cümlelerle. İyi yatırımcı iletişimi bu yolculuğa dayanacak şekilde yazılır: sunumun değil, sunumun rivayetinin tasarlanması. Bir iddianın piyasa değeri, ilk ağızdaki parlaklığından değil, ikinci ve üçüncü elde uğradığı kayıptan sonra elde kalanından okunur.",
      en: "Persuading the investor across the table does not end the work. That investor carries your story to an investment committee, the committee to a risk unit, the risk unit to a board — in rooms you will never enter, told by people less convinced than you, in sentences worse than yours. Good investor communication is written to survive that journey: you design not the pitch but its retelling. The market value of a claim is read not from its brilliance first-hand, but from what remains after the losses of the second and third telling.",
    },
  },
  {

    id: "insan-derinligi",
    term: { tr: "İnsan Derinliği", en: "Human Depth" },
    cats: ["yz", "insan"],
    short: {
      tr: "Algoritmaların kopyalayamadığı eleştirel düşünme, etik sezgi, yaratıcılık, empati toplamı.",
      en: "The sum of critical thinking, ethical intuition, creativity and empathy that algorithms cannot replicate.",
    },
    body: {
      tr: "YZ çağında salt teknik becerinin ötesinde, eleştirel düşünme, etik sezgi, yaratıcılık, farkındalık ve empatinin bütünü olarak tanımlanan insan yetkinlik seti. Becerilerin %39'unun 2030'a kadar eskiyeceği öngörülürken asıl tehlike teknik açık değil, insan derinliğinin görünmez kılınmasıdır.",
      en: "A set of human competencies defined in the AI age — beyond mere technical skill — as the sum of critical thinking, ethical intuition, creativity, awareness and empathy. With 39% of skills projected to be obsolete by 2030, the real risk is not a technical gap but human depth being rendered invisible.",
    },
  },
  {
    id: "itibar-guven-ayrimi",
    term: { tr: "İtibar–Güven Ayrımı", en: "Reputation–Trust Distinction" },
    cats: ["ilet"],
    short: {
      tr: "İtibar geçmişin birikimi; güven geleceğe verilen kredi.",
      en: "Reputation is the accumulation of the past; trust is a credit extended to the future.",
    },
    body: {
      tr: "İtibar geçmişin birikimi ve hikâyesidir; güven ise geleceğe dair henüz gerçekleşmemiş bir davranışa verilen kredi. İtibar tarihle, güven tutarlılıkla inşa edilir. Kurumsal iletişimde sıkça karıştırılan bu iki kavram, farklı yönetim stratejileri gerektirir.",
      en: "Reputation is the accumulation and story of the past; trust is a credit extended to a future behaviour that has not yet happened. Reputation is built with history, trust with consistency. Routinely conflated in corporate communication, the two require different management strategies.",
    },
  },
  {
    id: "jevons-paradoksu",
    term: { tr: "Jevons Paradoksu", en: "Jevons Paradox" },
    cats: ["surd", "iklim", "yz"],
    short: {
      tr: "Bir kaynağı kullanan teknoloji verimleştikçe, o kaynağın toplam tüketiminin azalmak yerine artması.",
      en: "When a technology that uses a resource becomes more efficient, total consumption of that resource rises rather than falls.",
    },
    body: {
      tr: "1865'te William Stanley Jevons'un kömür ve buhar makineleri üzerinden gözlemlediği sistemik geri besleme döngüsü. Verimlilik birim maliyeti düşürür; ucuzlayan faaliyet yaygınlaşır, yeni kullanım alanları ve yeni talep dalgaları yaratır; bu talep, kazanılan tasarrufu katbekat aşar. Yapay zekâ, veri merkezleri ve büyük dil modelleri çağında bu paradoks dijital ölçekte ve üstel bir hızla yeniden işliyor: her yeni verimlilik müjdesi yeni bir pazar işgalini finanse ediyor.",
      en: "The systemic feedback loop William Stanley Jevons observed in 1865 around coal and steam engines. Efficiency lowers the per-unit cost; cheaper activity spreads, finds new uses, and generates fresh waves of demand that more than offset the savings. In the age of AI, data centres, and large language models, the paradox returns at digital scale and exponential speed: every new efficiency announcement finances a new market expansion.",
    },
    related: [
      { slug: "verimlilik-maskeli-obur", label: { tr: "Verimlilik maskeli obur!", en: "The efficiency-masked glutton" }, to: "yazilar" },
    ],
  },
  {
    id: "kayitsizlik-mimarisi",
    term: { tr: "Kayıtsızlık Mimarisi", en: "Architecture of Indifference" },
    cats: ["surd", "ilet"],
    short: {
      tr: "Görmemek için kurulan kurumsal sistemlerin tasarımı.",
      en: "The design of institutional systems built in order not to see.",
    },
    body: {
      tr: "Tedarik zinciri opaklığı, sorumluluk dağıtan komiteler, kademeli onay zincirleri — sistem, kimsenin tek başına suçlanamayacağı şekilde kurgulanır. Kayıtsızlık mimarisi, ahlaki başarısızlığın değil mimari başarının ürünüdür.",
      en: "Opaque supply chains, committees that diffuse responsibility, layered approval chains — the system is engineered so that no single actor can be blamed alone. The architecture of indifference is the product of architectural success, not moral failure.",
    },
    related: [
      { slug: "kayitsizligin-dikenli-tahti", label: { tr: "Kayıtsızlığın Dikenli Tahtı", en: "Thorned Throne of Indifference" }, to: "dergi" },
    ],
  },
  {
    id: "kirilganlik-ekonomisi",
    term: { tr: "Kırılganlık Ekonomisi", en: "Fragility Economy" },
    cats: ["surd", "insan"],
    short: {
      tr: "Bireyin belirsizlikten yorulmasını bir gelir kaynağına çeviren ekonomik düzen.",
      en: "An economic order that monetises the citizen's exhaustion from permanent uncertainty.",
    },
    body: {
      tr: "Sistemik kırılganlıkların —iklim, yalnızlık, güvensizlik, epistemik çözülme— yeni piyasa kategorileri ve gelir modelleri ürettiği ekonomik düzen. Sorun ürünün hammaddesidir; çözüm vaadinin ticarileşmesi ise ürünün kendisi. Yalnızlık ekonomisi, ESG danışmanlığı ve dijital güvenlik pazarı bu çerçevenin üç ayrı tezahürüdür.",
      en: "An economic order in which systemic fragilities — climate, loneliness, distrust, epistemic dissolution — produce new market categories and revenue models. The problem is the raw material; the commercialised promise of a solution is the product itself. The loneliness economy, ESG consultancy and the digital security market are three expressions of the same frame.",
    },
  },
  {
    id: "kriz-ekonomisi",
    term: { tr: "Kriz Ekonomisi (Sürdürülebilirlik Paradoksu)", en: "Crisis Economy (Sustainability Paradox)" },
    cats: ["surd", "iklim"],
    short: {
      tr: "Sürdürülebilirlik sektörünün çözdüğünü iddia ettiği krizden beslenmesi.",
      en: "The sustainability industry feeding on the crisis it claims to solve.",
    },
    body: {
      tr: "Sürdürülebilirlik endüstrisinin çözdüğünü iddia ettiği krizden beslenmesi; ekolojik çöküş derinleştikçe sektörün büyümesi. Michel Serres'in \"parazit\" kavramının ESG bağlamına uygulanması. Sistem sonuçları değil süreçleri ödüllendirdiğinde, krizin kronikleşmesi sektörün varoluş koşuluna dönüşür.",
      en: "The sustainability industry living off the crisis it claims to resolve; the deeper the ecological collapse, the bigger the sector grows. Michel Serres's notion of the parasite applied to ESG. When the system rewards processes rather than outcomes, chronic crisis becomes the sector's condition of existence.",
    },
  },
  {
    id: "kurumsal-durus-bildirimi",
    term: { tr: "Kurumsal Duruş Bildirimi", en: "Corporate Posture Statement" },
    cats: ["ilet"],
    short: {
      tr: "Şirketin hangi anlatı içinde durduğunu, hangi sınırları çizdiğini açıkça ortaya koyması.",
      en: "A firm making explicit which narrative it stands within and which lines it draws.",
    },
    body: {
      tr: "Şirketin yalnızca ne ürettiğini değil hangi anlatının içinde yer aldığını, hangi dili güçlendirdiğini ve hangi sınırları çizdiğini açıkça ortaya koyan konumlanma. En güçlü mesaj kimi zaman söylenen değil, mesafe konulan şeydir. Baskı altında bile aynı kalmak, güveni belirleyen temel kriterdir.",
      en: "A positioning that makes explicit not only what a company produces but which narrative it inhabits, which language it reinforces and which limits it draws. The strongest message is sometimes not what is said but what is kept at a distance. Remaining the same under pressure is the decisive criterion of trust.",
    },
  },
  {
    id: "makulluk-tekeli",
    term: { tr: "Makullük Tekeli", en: "Monopoly of Reasonableness" },
    cats: ["ilet", "surd"],
    short: {
      tr: "Neyin \"gerçekçi\", \"ölçülü\" ve \"sorumlu\" sayılacağını dar bir merkezin belirlemesi; farklı çerçevelerin peşinen radikal ilan edilmesi.",
      en: "A narrow centre decides what counts as \"realistic\", \"measured\", or \"responsible\"; alternative framings are declared radical in advance.",
    },
    body: {
      tr: "Kamuoyu tartışması bir yelpazede değil, bir dış çember üzerinde yürür. İçeride durmak makul, dışında konuşmak \"ütopik\", \"provokatif\" ya da \"olgunlaşmamış\" sayılır. Makullük tekeli sansür değildir — ondan daha etkilidir: eleştiriyi baştan onaysız kılar. İklim politikasında \"gerçekçi hedef\", ekonomide \"sorumlu büyüme\", teknolojide \"dengeli düzenleme\" gibi ifadeler bu tekelin işaretleridir. Tartışmayı bastırmaz; tartışmanın çerçevesini kimin kuracağına karar verir. Radikal olan, çoğu zaman söylenen değil söylenmesine izin verilmeyendir.",
      en: "Public debate proceeds not along a spectrum but along an outer boundary. Speaking from inside is reasonable; speaking outside is dismissed as \"utopian\", \"provocative\" or \"immature\". The monopoly of reasonableness is not censorship — it is more effective: it renders critique disqualified in advance. Phrases such as \"realistic climate target\", \"responsible growth\", or \"balanced regulation\" are its markers. It does not suppress debate; it decides who gets to draw its frame. What is radical is often not what is said, but what is not permitted to be said.",
    },
  },
  {
    id: "metrik-kaymasi",
    term: { tr: "Metrik Kayması", en: "Metric Drift" },
    cats: ["surd", "iklim"],
    short: {
      tr: "Karmaşık bir hedefin tek bir sayıya indirgenmesiyle, sayının hedefin kendisini yerinden etmesi.",
      en: "A complex goal collapsed into a single number — and the number then displacing the goal.",
    },
    body: {
      tr: "Karmaşık bir hedefin tek bir sayıya indirgenmesiyle, o sayıyı düşürmenin yollarının hedefin kendisini yerinden etmesi. Ölçüm kolaylığı önem sırasını tersine çevirir. CO₂ ton/yıl rakamı düşürülürken sistemin genel ekolojik baskısı artabilir. Raporlama başarısı ile gerçek dönüşüm arasındaki uçurum buradan doğar.",
      en: "When a complex goal is collapsed into a single number, the means of lowering that number begin to displace the goal itself. Ease of measurement inverts priority. The CO₂-tons-per-year figure can fall while the system's overall ecological pressure rises. From here springs the gap between reporting success and real transformation.",
    },
  },
  {
    id: "ontolojik-kirilma",
    term: { tr: "Ontolojik Kırılma", en: "Ontological Rupture" },
    cats: ["yz"],
    short: {
      tr: "Bireyin algoritmik sistemler karşısında klasik anlamda \"özne\" olmaktan çıkışı.",
      en: "The individual's exit from being a classical \"subject\" in the face of algorithmic systems.",
    },
    body: {
      tr: "Algoritmik sistemler karşısında bireyin karar vermeme, bilgi temeline erişememe ve sorumluluğu taşıyamama durumunun, klasik anlamda \"özne\" olmaktan çıkışa yol açması. Teknik değil, varoluşsal bir kırılma. Habermas, Weber ve Kahneman'ın üç boyutlu kesişiminde oluşan kavram: akıl yürütme gerekçelendirilemez, rasyonellik dışsallaşır, karar yükü konforla örtülür.",
      en: "The individual's inability to decide, to access epistemic ground, or to carry responsibility before algorithmic systems leads to an exit from being a classical \"subject\". A rupture that is existential, not technical. The concept sits at the intersection of Habermas, Weber and Kahneman: reasoning cannot be justified, rationality is externalised, the burden of decision is hidden by comfort.",
    },
  },
  {
    id: "ortak-zaman",
    term: { tr: "Ortak Zaman", en: "Common Time" },
    cats: ["insan"],
    short: {
      tr: "Toplumun aynı ritmi ve aynı deneyimi eş zamanlı yaşaması; kaybedilmekte olan kapasite.",
      en: "Society sharing the same rhythm and experience simultaneously — a capacity being lost.",
    },
    body: {
      tr: "Toplumun aynı ritmi, aynı duraksamayı ve aynı deneyimi eş zamanlı yaşaması; sosyal senkronizasyonun birincil aracı. Modern hayatın hızlanması ve ekran tahkimatlı bireyselleşmesiyle yitirilmekte olan kapasite. Aynı sofrada oturup farklı ekranlara bakmak, fiziksel birlikteliğin artık ortak zaman üretmediğinin göstergesidir.",
      en: "A society living the same rhythm, the same pause and the same experience simultaneously; the primary medium of social synchronisation. A capacity now being lost as modern life accelerates and individualises behind screens. Sitting at the same table while looking at different screens shows that physical co-presence no longer produces common time.",
    },
  },
  {
    id: "rasyonalitenin-dissallastirilmasi",
    term: { tr: "Rasyonalitenin Dışsallaştırılması", en: "Externalisation of Rationality" },
    cats: ["yz"],
    short: {
      tr: "Akıl yürütmenin öznenin içinden çıkıp altyapının içine yerleşmesi.",
      en: "Reasoning migrating out of the subject and into the infrastructure.",
    },
    body: {
      tr: "Kararların mantığının öznenin içinde değil altyapının içinde konumlanması; insan rasyonel olmak yerine rasyonelliği sistemlere devrediyor. Weber modern dünyayı rasyonelleşme üzerinden okurdu; bugün yaşanan şey rasyonelleşmenin kendisi değil, onun dışsallaştırılmasıdır: süreç işliyor ama özne onu artık taşımıyor.",
      en: "The logic of decisions sits not within the subject but within the infrastructure; humans no longer become rational but hand rationality over to systems. Weber read modernity through rationalisation; what is happening today is not rationalisation itself but its externalisation: the process runs, but the subject no longer carries it.",
    },
  },
  {
    id: "riza-muhendisligi",
    term: { tr: "Rıza Mühendisliği", en: "Consent Engineering" },
    cats: ["yz", "ilet"],
    short: {
      tr: "İnsanın \"evet\" demesini bir arayüz problemine indirgemek.",
      en: "Reducing the human \"yes\" to an interface problem.",
    },
    body: {
      tr: "Çerez pencereleri, varsayılan tikler, koyu desenler ve sürtünme tasarımı — kullanıcının kabul edeceği şeyi sormadan önce belirler. Rıza mühendisliği, otoriterlikten farklı olarak kibarca işler; tercih hakkı görünürken seçim mimarisi tek bir çıktıyı zaten kararlaştırmıştır.",
      en: "Cookie banners, pre-ticked boxes, dark patterns, and friction design — they decide what the user will accept before asking. Consent engineering, unlike authoritarianism, operates politely; the right to choose appears intact while the choice architecture has already settled on a single outcome.",
    },
  },
  {
    id: "rebound-etkisi",
    term: { tr: "Rebound Etkisi (Geri Tepme)", en: "Rebound Effect" },
    cats: ["surd", "iklim", "yz"],
    short: {
      tr: "Verimlilikle kazanılan her birim tasarrufun, yeni talep ve yeni kapasiteyle geri dolması.",
      en: "Every unit of efficiency saving is refilled by new demand and new capacity.",
    },
    body: {
      tr: "Tasarruf edilen kaynak doğada ya da depoda bekleyerek yok olmaz; kapitalist büyüme motorunun bir sonraki vitesini finanse etmek üzere sermayeye dönüşür. Jevons Paradoksu'nun mikro mekanizması: birim maliyet düşer, yeni kullanım alanları açılır, daha önce o kaynağı talep etmeyen kitleler sisteme dahil olur ve toplam tüketim verimlilikle kazanılan tasarrufu aşar.",
      en: "Saved resources do not quietly disappear into nature or a warehouse; they turn into capital that finances the next gear of the capitalist growth engine. The micro-mechanism beneath Jevons Paradox: per-unit cost falls, new uses open, populations that did not previously demand the resource are pulled in, and total consumption overruns the saving that efficiency produced.",
    },
    related: [
      { slug: "verimlilik-maskeli-obur", label: { tr: "Verimlilik maskeli obur!", en: "The efficiency-masked glutton" }, to: "yazilar" },
    ],
  },
  {
    id: "sistemik-kurumsal-atalet",
    term: { tr: "Sistemik Kurumsal Atalet", en: "Systemic Corporate Inertia" },
    cats: ["iklim", "surd"],
    short: {
      tr: "Sürdürülebilirlik taahhüdü ile kurumu hazırlama arasındaki üç katlı uçurum.",
      en: "The triple gap between sustainability commitments and the institution actually being prepared for them.",
    },
    body: {
      tr: "CEO'ların sürdürülebilirlik taahhüdünde bulunurken kurumu bu taahhüde hazırlamayı başaramamasındaki yapısal boşluk. Söylem ile strateji, strateji ile operasyon arasındaki üç katlı uçurum. Bireysel yetersizlikten değil, kurumsal teşvik sistemlerinin kısa vadeli finansal performansı uzun vadeli dönüşümün önüne koymasından kaynaklanır.",
      en: "A structural gap in which CEOs make sustainability pledges while failing to prepare their organisations for them. A triple gap between discourse and strategy, strategy and operation. The cause is not individual incompetence but incentive systems that place short-term financial performance ahead of long-term transformation.",
    },
  },
  {
    id: "sonsuz-optimizasyon-miti",
    term: { tr: "Sonsuz Optimizasyon Miti", en: "Myth of Infinite Optimisation" },
    cats: ["insan", "yz"],
    short: {
      tr: "Her şeyin sürekli iyileştirilebileceği inancının yarattığı tükenmişlik teolojisi.",
      en: "The burnout theology produced by the belief that everything can be continuously improved.",
    },
    body: {
      tr: "Verim, uyku, dikkat, ilişki, atık, karbon — her değişken bir KPI'a dönüştüğünde insan kendine de bir tablo gibi davranmaya başlar. Sonsuz optimizasyon miti, sınırın bir başarısızlık değil bir hakikat olduğunu unutturur; yorgunluk, sistemin ürettiği değil bireyin başaramadığı bir şey gibi görünür.",
      en: "Productivity, sleep, attention, relationship, waste, carbon — once every variable becomes a KPI, the human begins treating themselves as a dashboard. The myth of infinite optimisation makes us forget that the limit is not a failure but a truth; fatigue starts to look like something the individual fell short of, not something the system produced.",
    },
    related: [
      { slug: "sonsuz-optimizasyon-mitine-direnis", label: { tr: "Sonsuz Optimizasyon Mitine Direniş", en: "Resistance to the Optimisation Myth" }, to: "dergi" },
    ],
  },
  {
    id: "sorumluluk-buharlasmasi",
    term: { tr: "Sorumluluk Buharlaşması", en: "Responsibility Evaporation" },
    cats: ["yz", "ilet"],
    short: {
      tr: "Algoritmik karar süreçlerinde failin belirsizleşmesi ve sorumluluğun ortadan kalkması.",
      en: "The agent dissolving in algorithmic decision processes — and responsibility evaporating with them.",
    },
    body: {
      tr: "Algoritmik karar süreçlerinde failin —yazılımcı, kurum, kullanıcı, veri seti sahibi— belirsizleşmesi; dağıtılan sorumluluğun pratikte ortadan kalkması. Eylem ile hesap verebilirlik arasındaki bağın çözülmesi. Herkes biraz sorumlu, kimse tam değil. İhmal değil, tasarım özelliğidir.",
      en: "In algorithmic decision processes the agent — developer, institution, user, dataset owner — blurs; distributed responsibility evaporates in practice. The link between action and accountability dissolves. Everyone is a little responsible, nobody fully so. Not negligence but a design feature.",
    },
  },
  {
    id: "sorumlulugun-bireye-devri",
    term: { tr: "Sorumluluğun Bireye Devri", en: "Devolution of Responsibility to the Individual" },
    cats: ["iklim", "surd"],
    short: {
      tr: "Sistemik bir krizin sorumluluğunun bireysel tüketici tercihlerine yıkılması.",
      en: "Loading systemic crisis responsibility onto individual consumer choices.",
    },
    body: {
      tr: "\"Karbon ayak izini hesapla\" gibi söylemlerin yapısal düzenlemeyi geciktirmenin en etkili yolu olarak işletilmesi. Bu stratejiyi 2000'lerin başında fosil yakıt endüstrisi yaygınlaştırdı. Kolektif bir sorunun bireysel ahlak meselesine indirgenmesi: sistemin en sorumlu boyutunu görünmez kılar, halkın siyasi baskı kapasitesini dağıtır.",
      en: "Slogans like \"calculate your carbon footprint\" are deployed as the most effective way to delay structural regulation — a strategy popularised by the fossil-fuel industry in the early 2000s. Reducing a collective problem to individual morality renders the system's most responsible dimension invisible and dissipates the public's capacity for political pressure.",
    },
  },
  {
    id: "strateji-olarak-karmasiklik",
    term: { tr: "Strateji Olarak Karmaşıklık", en: "Complexity as Strategy" },
    cats: ["surd", "iklim"],
    short: {
      tr: "ESG karmaşıklığının doğa kanunu değil, korunan bir tasarım seçimi olması.",
      en: "ESG complexity as a protected design choice, not a law of nature.",
    },
    body: {
      tr: "ESG çerçeveleri ve raporlama standartlarındaki karmaşıklığın doğa kanunu değil, sektör aktörlerinin standart belirleme süreçlerindeki varlığıyla korunan yapısal bir tasarım seçimi olması. Karmaşıklık, bağımlılık inşasının mimarisidir. GRI, TCFD, CSRD — her biri meşru bir amaca hizmet eder, ama bu düzeyde karmaşıklık zorunlu mu?",
      en: "The complexity of ESG frameworks and reporting standards is not a law of nature but a structural design choice, kept in place by industry actors' presence in standard-setting. Complexity is the architecture of dependency construction. GRI, TCFD, CSRD each serve legitimate purposes — but is this level of complexity really necessary?",
    },
  },
  {
    id: "surdurulebilirlik-fetisizmi",
    term: { tr: "Sürdürülebilirlik Fetişizmi", en: "Sustainability Fetishism" },
    cats: ["surd", "iklim"],
    short: {
      tr: "Kolektif ekolojik dönüşüm çağrısının tüketilen bir yaşam tarzına indirgenmesi.",
      en: "A collective call for ecological transformation reduced to a consumable lifestyle.",
    },
    body: {
      tr: "Kolektif ekolojik dönüşüm çağrısı olan sürdürülebilirliğin, tüketilen, etiketlenen ve içi boşaltılan bir yaşam tarzına dönüştürülmesi. Marx'ın meta fetişizmi kavramının yeşil pazarlama estetiğine uygulanması. Bu yeni yeşil ekonomide kriz çözülmedi; daha iyi bir ambalajla geri satıldı.",
      en: "Sustainability — originally a call for collective ecological transformation — is turned into a consumable, labelled and hollowed-out lifestyle. Marx's commodity fetishism applied to green marketing aesthetics. In this new green economy the crisis was not solved; it was repackaged and resold.",
    },
  },
  {
    id: "surdurulebilirlik-gurultusu",
    term: { tr: "Sürdürülebilirlik Gürültüsü", en: "Sustainability Noise" },
    cats: ["surd"],
    short: {
      tr: "Sürdürülebilirlik verisinin kanal kapasitesini aşarak anlam taşımaktan çıkması.",
      en: "Sustainability data exceeding channel capacity and ceasing to carry meaning.",
    },
    body: {
      tr: "Sürdürülebilirlik verisi ve raporlamasının kanal kapasitesini aşarak anlam taşımaktan çıkması. Shannon'ın enformasyon teorisindeki gürültü kavramının ESG bağlamına uygulanması: daha fazla rapor, daha az netlik. Ortalama bir halka açık şirketin sürdürülebilirlik verisi on yılda katlandı; bu artış daha iyi kararlara değil çoğu zaman eylemsizliğin meşrulaştırılmasına hizmet etti.",
      en: "Sustainability data and reporting exceeds channel capacity and stops carrying meaning. Shannon's notion of noise applied to ESG: more reports, less clarity. The sustainability data of an average listed company has multiplied over a decade; the increase has often served not better decisions but the legitimisation of inaction.",
    },
  },
  {
    id: "taahhut-borcu",
    term: { tr: "Taahhüt Borcu", en: "Commitment Debt" },
    cats: ["surd", "iklim"],
    short: {
      tr: "Bugün duyurulan sürdürülebilirlik hedeflerinin geleceğe yığılmış, gerçekleşme mecburiyeti belirsiz bir yükümlülük stokuna dönüşmesi.",
      en: "Today's sustainability pledges piling into a stock of obligations for the future, whose enforcement remains uncertain.",
    },
    body: {
      tr: "Şirketler ve devletler net-zero, 2030 hedefi, biyoçeşitlilik taahhüdü, kaynak azaltımı gibi bildirimleri hızla üretir; ama her yeni taahhüt, eskisinin denetimini erteleten bir söylemsel örtü işlevi görür. Taahhüt borcu karbon borcuyla aynı yapıya sahiptir: bilanço büyür, faiz işler, ödeme günü gelmeden yenisi yazılır. COP kararları, ESG duyuruları ve kurumsal 2050 planları çoğu zaman bu borcu ödemez, yalnızca taşır. Sonuç, imzalanmış ama takip edilmeyen bir gelecektir. İklim ve sürdürülebilirlik politikasında gerçek risk taahhüt eksikliği değil, taahhüt enflasyonudur.",
      en: "Corporations and states rapidly produce declarations — net-zero, 2030 targets, biodiversity commitments, resource reduction — but each new pledge functions as a discursive cover that defers scrutiny of the last one. Commitment debt has the same structure as carbon debt: the balance grows, interest accrues, and a new note is written before payment day arrives. COP decisions, ESG announcements, and corporate 2050 plans mostly do not settle this debt; they roll it over. The result is a future that has been signed for but never audited. The real risk in climate and sustainability policy is not a shortage of commitments but the inflation of them.",
    },
  },
  {
    id: "tasima-kapasitesi-sorusu",
    term: { tr: "Taşıma Kapasitesi Sorusu", en: "Carrying Capacity Question" },
    cats: ["surd", "iklim"],
    short: {
      tr: "\"Bu sistem kendi yükünü ne kadar süre taşıyabilir?\" — sürdürülebilirliğin etimolojik temeli.",
      en: "\"How long can this system carry its own load?\" — sustainability's etymological foundation.",
    },
    body: {
      tr: "Sürdürülebilirliğin Latince kökünden (sustinere: altta tutmak, taşımak) çıkan temel soru. Üç pratik alt soru üretir: Hangi kaynaklar tüketiliyor? Yenilenme hızı tüketim hızıyla uyumlu mu? Maliyetler kime, ne zaman, hangi coğrafyaya devrediliyor?",
      en: "The foundational question rooted in the Latin sustinere (to hold up, to bear). It produces three practical sub-questions: which resources are being consumed; is the renewal rate compatible with the consumption rate; and to whom, when and where are the costs being transferred?",
    },
  },
  {
    id: "temporal-arbitraj",
    term: { tr: "Temporal Arbitraj", en: "Temporal Arbitrage" },
    cats: ["iklim", "surd"],
    short: {
      tr: "Bugünün karını yarının yıkımıyla finanse etmenin sistematik adı.",
      en: "The systematic name for financing today's profit with tomorrow's collapse.",
    },
    body: {
      tr: "Karbon emisyonu, veri merkezi su tüketimi, sosyal medya bağımlılığı — hepsi aynı işlemi yapar: faydayı şimdiye, maliyeti geleceğe taşır. Temporal arbitraj, zaman ekseninde yapılan bir piyasa hilesidir; oyuncu, faturanın kendisine değil bir sonraki kuşağa geleceğini bildiği için kazanır.",
      en: "Carbon emissions, data centre water use, social-media dependency — all perform the same operation: they move benefit into the present and cost into the future. Temporal arbitrage is a market trick along the time axis; the player wins because they know the bill arrives not at their door but at the next generation's.",
    },
  },
  {
    id: "verimlilik-borcu",
    term: { tr: "Verimlilik Borcu", en: "Efficiency Debt" },
    cats: ["surd", "iklim", "yz"],
    short: {
      tr: "Bugünün verimlilik tasarrufunun, geleceğe ertelenmiş bir tüketim faturasına dönüşmesi.",
      en: "Today's efficiency savings turned into a consumption bill deferred to the future.",
    },
    body: {
      tr: "Teknoloji şirketlerinin sürdürülebilirlik raporlarında övünerek sunduğu her enerji tasarrufu, rebound etkisi yoluyla yarının devasa tüketim dalgalarından çekilmiş bir avans gibi işliyor. Verimlilik borcu, faturanın silindiğini değil yalnızca vadesinin uzatıldığını söyler: \"daha yeşil, daha az karbon salan yapay zekâ\" duyurusu eşzamanlı bir pazar genişlemesi beyanıdır; tüketimin freni değil, büyüme motorunun yüksek oktanlı yakıtıdır.",
      en: "Every energy saving that tech firms proudly showcase in their sustainability reports behaves, via the rebound effect, like an advance drawn against tomorrow's enormous waves of consumption. Efficiency debt says the bill has not been erased, only rescheduled: the announcement of a \"greener, lower-carbon AI\" is at the same time a declaration of market expansion — not a brake on consumption but high-octane fuel for the growth engine.",
    },
    related: [
      { slug: "verimlilik-maskeli-obur", label: { tr: "Verimlilik maskeli obur!", en: "The efficiency-masked glutton" }, to: "yazilar" },
    ],
  },
  {
    id: "washing-ekonomisi",
    term: { tr: "Washing Ekonomisi", en: "Washing Economy" },
    cats: ["surd", "ilet"],
    short: {
      tr: "Erdemin pazarlama bütçesine dönüştüğü, eylemden çok söylemin para kazandığı sistem.",
      en: "A system in which virtue becomes a marketing budget — discourse, not action, generates revenue.",
    },
    body: {
      tr: "Greenwashing → AI-washing → judgment-washing → upskill-washing → narrative-washing silsilesiyle genişleyen yapısal kalıp: gerçek dönüşüm yerine dönüşüm söyleminin ticarileşmesi. Her yeni gündem alanında aynı mimari yeniden üretiliyor. Washing tek tek şirketlerin taktik tercihi değil; sistemik bir üretim biçimidir.",
      en: "A structural pattern expanding through the series greenwashing → AI-washing → judgment-washing → upskill-washing → narrative-washing: the commercialisation of transformation discourse in place of real transformation. The same architecture is reproduced in each new agenda. Washing is not the tactical choice of individual firms; it is a systemic mode of production.",
    },
    related: [
      { slug: "kuresel-ikiyuzluluk", label: { tr: "Küresel İkiyüzlülük", en: "Global Hypocrisy" }, to: "dergi" },
      { slug: "surdurulebilirlik-fetisizmi", label: { tr: "Sürdürülebilirlik Fetişizmi", en: "Sustainability Fetishism" }, to: "dergi" },
    ],
  },
  {
    id: "yalnizlik-ekonomisi",
    term: { tr: "Yalnızlık Ekonomisi", en: "Loneliness Economy" },
    cats: ["insan", "yz"],
    short: {
      tr: "Bağ kuramamanın bir hizmet kategorisine dönüştüğü pazar.",
      en: "The market in which the inability to connect becomes a service category.",
    },
    body: {
      tr: "Yapay arkadaşlık uygulamaları, terapi abonelikleri, AI sevgilileri, sanal cenaze hizmetleri — yalnızlık ekonomisi acıyı çözmek için değil yönetmek için tasarlanır. Bağın yerine bağ simülasyonunu satar; iyileştikçe değil bağımlı kaldıkça kar eder.",
      en: "AI companions, therapy subscriptions, AI girlfriends, virtual funerals — the loneliness economy is designed not to resolve pain but to manage it. It sells the simulation of bond instead of bond itself; it profits not as you heal but as you remain dependent.",
    },
  },
  {
    id: "yanki-odasi",
    term: { tr: "Yankı Odası (Algoritmik)", en: "Echo Chamber (Algorithmic)" },
    cats: ["ilet", "yz"],
    short: {
      tr: "Bireylerin kendi inançlarını doğrulayan içeriklerle çevrelendiği dijital uzam.",
      en: "A digital space surrounding individuals with content that confirms their existing beliefs.",
    },
    body: {
      tr: "Algoritmaların katılım optimizasyonu sonucu bireyleri kendi inançlarını doğrulayan içeriklerle çevrelediği enformasyon ortamı; gerçeğin değil, güçlendirilmiş önyargının yaşandığı dijital uzam. Kamusal tartışmanın ritmi dikkatli akıl yürütmeyle değil algoritmik görünürlük ekonomisiyle şekilleniyor.",
      en: "An information environment in which engagement-optimising algorithms surround individuals with content that confirms their beliefs; a digital space where what is experienced is not truth but reinforced bias. The rhythm of public debate is shaped by the algorithmic visibility economy, not by careful reasoning.",
    },
  },
  {
    id: "yesil-anlati-ele-gecirilmesi",
    term: { tr: "Yeşil Anlatı Ele Geçirilmesi", en: "Capture of the Green Narrative" },
    cats: ["iklim", "surd"],
    short: {
      tr: "\"Net sıfır\", \"karbon nötr\", \"yeşil büyüme\"nin pazarlama sloganına dönüştürülmesi.",
      en: "\"Net zero\", \"carbon neutral\" and \"green growth\" being turned into marketing slogans.",
    },
    body: {
      tr: "Kavramların zamanla pazarlama sloganına dönüştürülmesi ve ihanet ettiği değerlerin üstünü örtmesi. Dilin ele geçirilmesiyle mücadelenin zemini de çalınır. Brundtland Raporu'nun pusula olarak kurduğu çerçeve, kırk yılda kurumsal uyum belgesi ve reklam dili haline geldi.",
      en: "Concepts are gradually turned into marketing slogans, covering over the values they betray. When the language is captured, the ground for struggle is also stolen. The compass set by the Brundtland Report has, over forty years, become a corporate compliance document and an advertising vernacular.",
    },
  },
  {
    id: "yeterlik-ilkesi",
    term: { tr: "Yeterlik İlkesi (Sufficiency)", en: "Sufficiency Principle" },
    cats: ["iklim", "surd"],
    short: {
      tr: "Daha verimli üretmekten önce daha az tüketmek — sürdürülebilirliğin en görünmez ilkesi.",
      en: "Consuming less before producing more efficiently — sustainability's most invisible principle.",
    },
    body: {
      tr: "Daha verimli üretmekten önce daha az tüketmenin sahici sürdürülebilirliğin temeli olduğu ilke. Mevcut büyüme modeliyle en zor uzlaşan ilkedir; bu yüzden sürdürülebilirlik söyleminde en görünmez olanıdır. Verimlilik artışı tüketim hacmini azaltmadığında \"yeniden sıçrama etkisi\" devreye girer.",
      en: "The principle that consuming less, before producing more efficiently, is the real foundation of sustainability. It is the principle most at odds with the current growth model — and therefore the most invisible in sustainability discourse. When efficiency gains do not reduce consumption volume, the rebound effect kicks in.",
    },
  },
];

function firstLetter(s: string): string {
  const ch = s.trim().charAt(0).toLocaleUpperCase("tr-TR");
  return ch || "?";
}

const TR_ALPHABET = [
  "A","B","C","Ç","D","E","F","G","H","I","İ","J","K","L","M","N","O","Ö","P","R","S","Ş","T","U","Ü","V","W","Y","Z",
];

export function LexiconPage({ lang }: { lang: Lang }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Category | "all">("all");

  const sorted = useMemo(() => {
    return [...ENTRIES].sort((a, b) =>
      a.term[lang].localeCompare(b.term[lang], lang === "tr" ? "tr" : "en"),
    );
  }, [lang]);

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase(lang === "tr" ? "tr" : "en");
    return sorted.filter((e) => {
      const catOk = filter === "all" || e.cats.includes(filter);
      if (!catOk) return false;
      if (!q) return true;
      const hay = (
        e.term[lang] + " " + e.short[lang] + " " + e.body[lang]
      ).toLocaleLowerCase(lang === "tr" ? "tr" : "en");
      return hay.includes(q);
    });
  }, [sorted, filter, query, lang]);

  const groups = useMemo(() => {
    const m = new Map<string, typeof filtered>();
    for (const e of filtered) {
      const l = firstLetter(e.term[lang]);
      if (!m.has(l)) m.set(l, []);
      m.get(l)!.push(e);
    }
    return m;
  }, [filtered, lang]);

  const availableLetters = useMemo(
    () => new Set(sorted.map((e) => firstLetter(e.term[lang]))),
    [sorted, lang],
  );

  const title =
    lang === "tr"
      ? "Sözlük — Kavramsal Çerçeveler"
      : "Lexicon — Conceptual Frameworks";
  const intro =
    lang === "tr"
      ? "Bu sözlük, yazılar boyunca tekrar eden ya da burada ilk kez adlandırılan kavramları bir arada tutmak için yazıldı. Tanımlar ansiklopedik değil; bir bakış açısının iskeletini ortaya koyar. Her madde, başka yazılarla okunmak üzere düşünülmüştür."
      : "This lexicon gathers concepts that recur across the essays — or that are named here for the first time. The definitions are not encyclopaedic; they sketch the skeleton of a way of seeing. Each entry is written to be read alongside other essays.";

  return (
    <div className="mx-auto max-w-7xl px-5 pb-12 pt-5 sm:px-8 lg:pt-7">
      {lang === "tr" && <Breadcrumbs items={[{ label: "Sözlük" }]} />}
      <header className="mt-5 grid gap-5 lg:mt-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-end lg:gap-12">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-ink/80">
            <span aria-hidden className="size-2.5 rounded-full bg-ink" />
            {lang === "tr" ? "Düşünce Haritası" : "Map of Ideas"}
          </p>
          <h1 className="mt-3 font-serif text-[44px] font-medium leading-[0.95] sm:text-[64px] lg:text-[72px]">
            {lang === "tr" ? (<>Kavramsal çerçeveler<br className="hidden sm:block" /> sözlüğü</>) : (<>A lexicon of conceptual frameworks</>)}
          </h1>
          <span aria-hidden className="mt-5 block h-1 w-12 bg-ink" />
        </div>
        <p className="max-w-xl font-serif text-[17px] leading-snug text-ink/80 sm:text-[19px]">{intro}</p>
      </header>

      {/* Controls */}
      <div className="sticky top-0 z-20 -mx-5 mt-8 border-y border-ink/10 bg-paper/95 px-5 py-3 backdrop-blur sm:-mx-8 sm:px-8 lg:mt-12">
        <div className="grid gap-3 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:items-center lg:gap-8">
          <label className="flex min-h-11 items-center gap-3 border-b border-ink/40 focus-within:border-ink">
            <Search aria-hidden className="size-4 shrink-0 text-ink/70" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={lang === "tr" ? "Kavram ara" : "Search a concept"}
              aria-label={lang === "tr" ? "Kavram ara" : "Search a concept"}
              className="min-w-0 flex-1 bg-transparent font-serif text-[16px] text-ink placeholder:text-ink/60 focus:outline-none"
            />
          </label>
          <div className="-mx-5 flex gap-1 overflow-x-auto px-5 sm:mx-0 sm:flex-wrap sm:px-0">
            {(["all", "yz", "surd", "insan", "ilet", "iklim"] as const).map((c) => {
              const active = filter === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setFilter(c)}
                  aria-pressed={active}
                  className={`min-h-10 shrink-0 whitespace-nowrap px-3 font-serif text-[15px] transition-colors ${active ? "text-ink underline decoration-ink/60 underline-offset-8" : "text-ink/65 hover:text-ink"}`}
                >
                  {c === "all" ? (lang === "tr" ? "Tümü" : "All") : CATEGORY_LABEL[c][lang]}
                </button>
              );
            })}
          </div>
        </div>
        <nav aria-label={lang === "tr" ? "Alfabetik dizin" : "Alphabetic index"} className="-mx-5 mt-2 flex gap-0.5 overflow-x-auto px-5 sm:mx-0 sm:flex-wrap sm:px-0">
          {TR_ALPHABET.map((l) =>
            availableLetters.has(l) ? (
              <a key={l} href={`#letter-${l}`} className="grid size-8 shrink-0 place-items-center font-serif text-[15px] text-ink hover:bg-secondary">{l}</a>
            ) : (
              <span key={l} aria-hidden className="grid size-8 shrink-0 place-items-center font-serif text-[15px] text-ink/25">{l}</span>
            ),
          )}
        </nav>
      </div>

      <p className="mt-6 text-xs text-ink/70">
        {filtered.length === sorted.length
          ? lang === "tr" ? `${sorted.length} kavram` : `${sorted.length} concepts`
          : lang === "tr" ? `${filtered.length} kavram gösteriliyor` : `Showing ${filtered.length} concepts`}
      </p>

      {filtered.length === 0 ? (
        <p className="py-20 text-center font-serif text-xl text-ink/75">
          {lang === "tr" ? "Bu arama için kavram bulunamadı." : "No concept found for this search."}
        </p>
      ) : (
        <div className="mt-4">
          {[...groups.entries()].map(([letter, items]) => (
            <section key={letter} id={`letter-${letter}`} aria-label={letter} className="scroll-mt-40 grid gap-x-10 border-t border-ink/10 py-6 md:grid-cols-[5rem_minmax(0,1fr)] lg:py-8">
              <div className="font-serif text-[40px] leading-none text-ink/80 md:text-[56px]">{letter}</div>
              <ul className="divide-y divide-ink/10">
                {items.map((e) => (
                  <li key={e.id} id={e.id} className="scroll-mt-40 py-5 first:pt-3 md:first:pt-0">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-ink/65">
                      {e.cats.map((c) => CATEGORY_LABEL[c][lang]).join(" · ")}
                    </p>
                    <h2 className="mt-1.5 font-serif text-[22px] font-medium leading-tight sm:text-[26px]">{e.term[lang]}</h2>
                    <p className="mt-2 max-w-3xl font-serif text-[17px] leading-snug text-ink/85 sm:text-[19px]">{e.short[lang]}</p>
                    <p className="mt-2 max-w-3xl text-[15px] leading-relaxed text-ink/75">{e.body[lang]}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-x-6">
                      {(isPilot(e.id) || lang === "tr") && (
                        <Link
                          to={lang === "tr" ? "/sozluk/$slug" : "/glossary/$slug"}
                          params={{ slug: pilotSlug(e.id, lang) }}
                          className="inline-flex min-h-11 items-center gap-2 font-serif text-[15px] text-theme-tech hover:underline"
                        >
                          {lang === "tr" ? "Kavram sayfası" : "Full definition page"} <ArrowRight aria-hidden className="size-4" />
                        </Link>
                      )}
                      {e.related && e.related.length > 0 && (
                        <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                          <span className="text-ink/60">{lang === "tr" ? "Okuma:" : "Read:"}</span>
                          {e.related.map((r) =>
                            r.to === "dergi" ? (
                              <Link key={r.slug} to="/dergi/$slug" params={{ slug: r.slug }} className="font-serif text-ink underline decoration-ink/30 underline-offset-4 hover:text-clay">{r.label[lang]}</Link>
                            ) : (
                              <Link key={r.slug} to="/yazilar/$slug" params={{ slug: r.slug }} className="font-serif text-ink underline decoration-ink/30 underline-offset-4 hover:text-clay">{r.label[lang]}</Link>
                            ),
                          )}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <section className="mt-6 grid gap-4 border-t border-ink/10 bg-secondary px-5 py-8 sm:px-8 lg:grid-cols-[1fr_auto] lg:items-center">
        <p className="max-w-2xl font-serif text-[19px] leading-snug text-ink/85 sm:text-[22px]">
          {lang === "tr"
            ? "Bir kavram, başkalarına gönderildiğinde olgunlaşır. Eklenmesini istediğiniz bir madde varsa yazın; sözlük canlı bir belgedir."
            : "A concept matures when it is sent to others. If there is an entry you would like added, write to me; this lexicon is a living document."}
        </p>
        <Link to="/hakkimda" className="inline-flex min-h-11 items-center gap-2 font-serif text-[15px] text-ink hover:text-clay">
          {lang === "tr" ? "İletişim" : "Get in touch"} <ArrowRight aria-hidden className="size-4" />
        </Link>
      </section>
    </div>
  );
}

export function lexiconHead(lang: Lang, opts?: { variant?: "index" | "unknown" }) {
  // English lexicon content is canonical on the English brand domain
  // (/concepts); the Turkish one on the Turkish domain.
  const url =
    lang === "tr"
      ? "https://akillisurdurulebilirlik.com/sozluk"
      : "https://smartsustainability.ai/concepts";
  const unknown = opts?.variant === "unknown";

  if (unknown) {
    // Splat route: an unrecognised term path. Unique, non-duplicate metadata
    // plus noindex so it never competes with the lexicon index.
    const title =
      lang === "tr"
        ? pageTitle("Kavram bulunamadı — Sözlük", "tr")
        : pageTitle("Term not found — Lexicon", "en");
    const description =
      lang === "tr"
        ? "Aradığınız kavram sözlükte bulunamadı. Sözlük dizininden tüm özgün kavramlara göz atabilirsiniz."
        : "The concept you requested is not in the lexicon. Browse the lexicon index for all original concepts.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "noindex, follow" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
      ...(lang === "tr" ? [
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        ...socialImageMeta(),
      ] : []),
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:locale", content: lang === "tr" ? "tr_TR" : "en_US" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  }

  const title =
    lang === "tr"
      ? pageTitle("Sözlük — Özgün Kavramlar", "tr")
      : pageTitle("Lexicon — Original Concepts", "en");
  const description =
    lang === "tr"
      ? "Kırılganlık ekonomisi, washing ekonomisi, epistemik silahlanma, temporal arbitraj — Arda Öztaşkın'ın yazılarındaki özgün kavramların sözlüğü."
      : "Fragility economy, washing economy, epistemic weaponisation, temporal arbitrage — a lexicon of original concepts from Arda Öztaşkın's essays.";

  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      ...(lang === "tr" ? [
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        ...socialImageMeta(),
      ] : []),
      { property: "og:type", content: "website" },
      { property: "og:url", content: url },
      { property: "og:locale", content: lang === "tr" ? "tr_TR" : "en_US" },
    ],
    links: [
      { rel: "canonical", href: url },
      ...hreflangLinks("/sozluk", "/concepts"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "DefinedTermSet",
          name: title,
          description,
          url,
          inLanguage: lang === "tr" ? "tr-TR" : "en-US",
          hasDefinedTerm: ENTRIES.map((e) => ({
            "@type": "DefinedTerm",
            "@id": `${url}#${e.id}`,
            name: e.term[lang],
            description: e.short[lang],
            inDefinedTermSet: url,
          })),
        }),
      },
    ],
  };
}
